import { useState, useEffect, useRef, useMemo, useCallback } from 'react';
import { format } from 'date-fns';
import { es } from 'date-fns/locale';
import { TopBar } from '../components/layout/TopBar';
import { useAppStore } from '../store/useStore';
import { tablesAPI, zonesAPI, reservationsAPI } from '../lib/api';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { toast } from 'sonner';
import { cn } from '../lib/utils';
import {
  Plus, Trash2, RotateCw, Save, Eye, Move, Grid3X3, ZoomIn, ZoomOut,
  RefreshCw, Loader2, X, Settings2, Users, Clock, Calendar,
  ChevronLeft, ChevronRight, Pencil, MousePointer2, Hand, Copy
} from 'lucide-react';

// ─── Constants ────────────────────────────────────────────────────────────────
const GRID = 20;
const snap = v => Math.round(v / GRID) * GRID;

const ZONE_PALETTE = [
  '#F97316','#3B82F6','#10B981','#8B5CF6','#EAB308',
  '#EC4899','#06B6D4','#EF4444','#84CC16','#6B7280',
];

const TABLE_SHAPES = [
  { id: 'round',     label: 'Redonda',    w: 80, h: 80 },
  { id: 'square',    label: 'Cuadrada',   w: 80, h: 80 },
  { id: 'rect4',     label: '4 pers.',    w: 120, h: 80 },
  { id: 'rect6',     label: '6 pers.',    w: 160, h: 80 },
  { id: 'rect8',     label: '8 pers.',    w: 200, h: 80 },
  { id: 'round_big', label: 'Mesa grande',w: 120, h: 120 },
];

const STATUS_CFG = {
  free:      { bg: '#ffffff', border: '#cbd5e1', chair: '#e2e8f0', text: '#64748b', label: 'Libre' },
  pending:   { bg: '#fffbeb', border: '#fcd34d', chair: '#fde68a', text: '#92400e', label: 'Pendiente' },
  confirmed: { bg: '#ecfdf5', border: '#34d399', chair: '#6ee7b7', text: '#065f46', label: 'Confirmada' },
  seated:    { bg: '#eff6ff', border: '#60a5fa', chair: '#93c5fd', text: '#1d4ed8', label: 'En mesa' },
  completed: { bg: '#f0fdfa', border: '#2dd4bf', chair: '#5eead4', text: '#0d9488', label: 'Completada' },
};

// ─── Chair dots around a table ─────────────────────────────────────────────────
function Chairs({ count, tableW, tableH, isRound, chairColor }) {
  const chairs = [];
  const cr = 7; // chair radius
  const gap = 6;

  if (isRound) {
    const r = tableW / 2 + gap + cr;
    const n = Math.min(count, 12);
    for (let i = 0; i < n; i++) {
      const angle = (i / n) * 2 * Math.PI - Math.PI / 2;
      chairs.push({ x: tableW / 2 + r * Math.cos(angle) - cr, y: tableH / 2 + r * Math.sin(angle) - cr });
    }
  } else {
    const perSide = Math.ceil(count / 2);
    const spacing = (tableW - 20) / Math.max(perSide, 1);
    for (let i = 0; i < Math.min(perSide, 5); i++) {
      chairs.push({ x: 10 + i * spacing, y: -(gap + cr * 2) });
      chairs.push({ x: 10 + i * spacing, y: tableH + gap });
    }
  }

  return (
    <>
      {chairs.map((c, i) => (
        <div key={i} className="absolute rounded-full border"
          style={{
            width: cr * 2, height: cr * 2, left: c.x, top: c.y,
            backgroundColor: chairColor,
            borderColor: chairColor,
            boxShadow: 'inset 0 1px 2px rgba(0,0,0,0.1)',
          }} />
      ))}
    </>
  );
}

// ─── Table on Canvas ──────────────────────────────────────────────────────────
function TableNode({ table, res, selected, hovered, onSelect, onHover, onDragStart, mode }) {
  const shape = TABLE_SHAPES.find(s => s.id === table.shape) || TABLE_SHAPES[0];
  const isRound = table.shape === 'round' || table.shape === 'round_big';
  const w = table.width || shape.w;
  const h = table.height || shape.h;
  const rotation = table.rotation || 0;
  const st = res ? (STATUS_CFG[res.status] || STATUS_CFG.free) : STATUS_CFG.free;

  return (
    <div
      className={cn(
        "absolute group select-none transition-shadow duration-150",
        mode === 'edit' ? "cursor-grab active:cursor-grabbing" : "cursor-default",
      )}
      style={{
        left: table.x || 60,
        top: table.y || 60,
        width: w + 32,  // extra space for chairs
        height: h + 32,
        padding: 16,
        zIndex: selected ? 20 : hovered ? 10 : 2,
      }}
      onMouseDown={e => { e.stopPropagation(); onSelect(table.id); if (mode === 'edit') onDragStart(e, table.id); }}
      onMouseEnter={() => onHover(table.id)}
      onMouseLeave={() => onHover(null)}
    >
      {/* Chairs layer */}
      <div className="absolute inset-0" style={{ transform: `rotate(${rotation}deg)` }}>
        <Chairs
          count={table.capacity_max || 4}
          tableW={w} tableH={h}
          isRound={isRound}
          chairColor={st.chair}
        />
      </div>

      {/* Table surface */}
      <div
        className={cn(
          "absolute flex flex-col items-center justify-center transition-all duration-150",
          selected && "ring-2 ring-orange-500 ring-offset-2",
          hovered && !selected && "ring-2 ring-blue-400 ring-offset-1",
        )}
        style={{
          left: 16, top: 16, width: w, height: h,
          borderRadius: isRound ? '50%' : '10px',
          backgroundColor: st.bg,
          border: `2.5px solid ${st.border}`,
          transform: `rotate(${rotation}deg)`,
          boxShadow: selected
            ? '0 0 0 3px rgba(249,115,22,0.3), 0 4px 12px rgba(0,0,0,0.15)'
            : hovered
            ? '0 4px 12px rgba(0,0,0,0.12)'
            : '0 2px 4px rgba(0,0,0,0.06)',
        }}
      >
        <span className="text-[11px] font-bold leading-tight text-center px-1" style={{ color: st.text }}>
          {table.name}
        </span>
        <span className="text-[9px] mt-0.5 opacity-70" style={{ color: st.text }}>
          {table.capacity_max}p
        </span>
        {res?.guest_name && (
          <span className="text-[9px] mt-0.5 font-medium leading-tight text-center px-1 truncate max-w-full"
            style={{ color: st.text }}>
            {res.guest_name.split(' ')[0]}
          </span>
        )}
      </div>

      {/* Hover tooltip */}
      {hovered && (
        <div className="absolute z-50 pointer-events-none"
          style={{ bottom: h + 40, left: '50%', transform: 'translateX(-50%)' }}>
          <div className="bg-slate-900 text-white text-[11px] rounded-lg px-3 py-2 shadow-xl whitespace-nowrap">
            <p className="font-semibold">{table.name}</p>
            <p className="text-slate-300">Cap: {table.capacity_min || 1}–{table.capacity_max || 4} pers.</p>
            {res ? (
              <>
                <div className="border-t border-slate-700 mt-1 pt-1">
                  <p className="font-medium">{res.guest_name}</p>
                  <p className="text-slate-300">{res.time} · {res.party_size}p · {STATUS_CFG[res.status]?.label}</p>
                </div>
              </>
            ) : (
              <p className="text-emerald-400">Libre</p>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

// ─── Zone on Canvas ────────────────────────────────────────────────────────────
function ZoneBlock({ zone, tables }) {
  const zoneTables = tables.filter(t => t.zone_id === zone.id);
  if (!zoneTables.length) return null;
  const xs = zoneTables.map(t => (t.x || 60));
  const ys = zoneTables.map(t => (t.y || 60));
  const maxW = Math.max(...zoneTables.map(t => (t.width || 80)));
  const maxH = Math.max(...zoneTables.map(t => (t.height || 80)));
  const left = Math.min(...xs) - 40;
  const top = Math.min(...ys) - 40;
  const right = Math.max(...xs) + maxW + 40 + 32;
  const bottom = Math.max(...ys) + maxH + 40 + 32;

  return (
    <div className="absolute rounded-2xl pointer-events-none"
      style={{
        left, top,
        width: right - left,
        height: bottom - top,
        backgroundColor: (zone.color || '#6B7280') + '12',
        border: `2px dashed ${zone.color || '#6B7280'}50`,
      }}>
      <div className="absolute top-2 left-3 flex items-center gap-1.5">
        <div className="w-2 h-2 rounded-full" style={{ backgroundColor: zone.color || '#6B7280' }} />
        <span className="text-[11px] font-semibold" style={{ color: zone.color || '#6B7280', opacity: 0.8 }}>
          {zone.name}
        </span>
      </div>
    </div>
  );
}

// ─── Add Table Dialog ─────────────────────────────────────────────────────────
function AddTableDialog({ open, onClose, zones, onSave, canvasX, canvasY }) {
  const [form, setForm] = useState({
    name: '', capacity_min: 2, capacity_max: 4, shape: 'square', zone_id: '',
  });
  const [saving, setSaving] = useState(false);

  const selectedShape = TABLE_SHAPES.find(s => s.id === form.shape) || TABLE_SHAPES[1];

  const save = async () => {
    if (!form.name.trim()) { toast.error('Nombre requerido'); return; }
    setSaving(true);
    try {
      await tablesAPI.create({
        ...form,
        x: snap(canvasX || 100),
        y: snap(canvasY || 100),
        width: selectedShape.w,
        height: selectedShape.h,
      });
      toast.success(`Mesa "${form.name}" agregada`);
      onSave();
      onClose();
      setForm({ name: '', capacity_min: 2, capacity_max: 4, shape: 'square', zone_id: '' });
    } catch (e) { toast.error(e?.response?.data?.detail || 'Error'); }
    finally { setSaving(false); }
  };

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-sm">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Plus className="h-4 w-4 text-orange-500" /> Nueva mesa
          </DialogTitle>
        </DialogHeader>
        <div className="space-y-4 py-1">
          <div>
            <Label className="text-xs text-slate-500 mb-1 block">Nombre *</Label>
            <Input value={form.name} onChange={e => setForm(f => ({ ...f, name: e.target.value }))}
              placeholder="Mesa 1, VIP, Barra A..." className="text-sm" autoFocus />
          </div>

          <div>
            <Label className="text-xs text-slate-500 mb-2 block">Forma y tamaño</Label>
            <div className="grid grid-cols-3 gap-2">
              {TABLE_SHAPES.map(s => (
                <button key={s.id} onClick={() => setForm(f => ({ ...f, shape: s.id }))}
                  className={cn("py-2 px-1 rounded-lg border text-[11px] text-center font-medium transition-all",
                    form.shape === s.id
                      ? "border-orange-500 bg-orange-50 text-orange-700"
                      : "border-slate-200 text-slate-600 hover:border-slate-300")}>
                  {s.label}
                </button>
              ))}
            </div>
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label className="text-xs text-slate-500 mb-1 block">Cap. mínima</Label>
              <Input type="number" min={1} value={form.capacity_min}
                onChange={e => setForm(f => ({ ...f, capacity_min: +e.target.value || 1 }))} className="text-sm h-9" />
            </div>
            <div>
              <Label className="text-xs text-slate-500 mb-1 block">Cap. máxima</Label>
              <Input type="number" min={1} value={form.capacity_max}
                onChange={e => setForm(f => ({ ...f, capacity_max: +e.target.value || 1 }))} className="text-sm h-9" />
            </div>
          </div>

          <div>
            <Label className="text-xs text-slate-500 mb-1 block">Zona</Label>
            <Select value={form.zone_id || 'none'} onValueChange={v => setForm(f => ({ ...f, zone_id: v === 'none' ? '' : v }))}>
              <SelectTrigger className="text-sm h-9"><SelectValue placeholder="Sin zona" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="none">Sin zona</SelectItem>
                {zones.map(z => (
                  <SelectItem key={z.id} value={z.id}>
                    <div className="flex items-center gap-2">
                      <div className="w-2 h-2 rounded-full" style={{ backgroundColor: z.color || '#6b7280' }} />
                      {z.name}
                    </div>
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Cancelar</Button>
          <Button onClick={save} disabled={saving} className="bg-orange-500 hover:bg-orange-600 text-white">
            {saving ? <Loader2 className="h-4 w-4 animate-spin mr-1" /> : <Plus className="h-4 w-4 mr-1" />}
            Agregar mesa
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// ─── Manage Zones Dialog ──────────────────────────────────────────────────────
function ZonesDialog({ open, onClose, zones, onRefresh }) {
  const [newName, setNewName] = useState('');
  const [newColor, setNewColor] = useState(ZONE_PALETTE[0]);
  const [creating, setCreating] = useState(false);

  const createZone = async () => {
    if (!newName.trim()) { toast.error('Nombre requerido'); return; }
    setCreating(true);
    try {
      await zonesAPI.create({ name: newName.trim(), color: newColor });
      toast.success(`Zona "${newName}" creada`);
      setNewName('');
      onRefresh();
    } catch { toast.error('Error al crear zona'); }
    finally { setCreating(false); }
  };

  const deleteZone = async (id) => {
    if (!window.confirm('¿Eliminar esta zona? Las mesas no se eliminarán.')) return;
    try {
      await zonesAPI.delete(id);
      toast.success('Zona eliminada');
      onRefresh();
    } catch { toast.error('Error al eliminar zona'); }
  };

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-sm">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Grid3X3 className="h-4 w-4 text-orange-500" /> Gestionar zonas
          </DialogTitle>
        </DialogHeader>
        <div className="space-y-4 py-1">
          {/* Create zone */}
          <div className="p-3 bg-slate-50 rounded-xl space-y-2">
            <Label className="text-xs font-medium text-slate-600 block">Nueva zona</Label>
            <div className="flex gap-2">
              <Input value={newName} onChange={e => setNewName(e.target.value)}
                placeholder="Nombre..." className="text-sm flex-1 h-8" />
              <input type="color" value={newColor} onChange={e => setNewColor(e.target.value)}
                className="h-8 w-10 rounded border border-slate-200 cursor-pointer p-0.5 flex-shrink-0" />
              <Button size="sm" onClick={createZone} disabled={creating}
                className="h-8 bg-orange-500 hover:bg-orange-600 text-white px-2">
                {creating ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Plus className="h-3.5 w-3.5" />}
              </Button>
            </div>
            <div className="flex flex-wrap gap-1.5">
              {ZONE_PALETTE.map(c => (
                <button key={c} onClick={() => setNewColor(c)}
                  className={cn("w-5 h-5 rounded-full border-2 transition-all",
                    newColor === c ? "border-slate-800 scale-110" : "border-transparent")}
                  style={{ backgroundColor: c }} />
              ))}
            </div>
          </div>

          {/* Existing zones */}
          <div className="space-y-1 max-h-52 overflow-y-auto">
            {zones.length === 0 ? (
              <p className="text-xs text-slate-400 text-center py-4">Sin zonas creadas</p>
            ) : zones.map(z => (
              <div key={z.id} className="flex items-center justify-between px-3 py-2 rounded-lg hover:bg-slate-50">
                <div className="flex items-center gap-2">
                  <div className="w-3 h-3 rounded-full" style={{ backgroundColor: z.color || '#6b7280' }} />
                  <span className="text-sm font-medium text-slate-700">{z.name}</span>
                </div>
                <button onClick={() => deleteZone(z.id)} className="p-1 rounded hover:bg-red-100 text-red-400 hover:text-red-600">
                  <Trash2 className="h-3.5 w-3.5" />
                </button>
              </div>
            ))}
          </div>
        </div>
        <DialogFooter><Button variant="outline" onClick={onClose}>Cerrar</Button></DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// ─── Table Properties Panel ────────────────────────────────────────────────────
function PropertiesPanel({ table, res, zones, onUpdate, onDelete, onClose }) {
  const [editing, setEditing] = useState(false);
  const [form, setForm] = useState({ name: table.name, capacity_min: table.capacity_min, capacity_max: table.capacity_max, zone_id: table.zone_id || '', shape: table.shape || 'square' });
  const [saving, setSaving] = useState(false);

  const save = async () => {
    setSaving(true);
    try {
      await tablesAPI.update(table.id, form);
      toast.success('Mesa actualizada');
      onUpdate();
      setEditing(false);
    } catch { toast.error('Error al guardar'); }
    finally { setSaving(false); }
  };

  const handleDelete = async () => {
    if (!window.confirm(`¿Eliminar "${table.name}"?`)) return;
    try {
      await tablesAPI.delete(table.id);
      toast.success('Mesa eliminada');
      onDelete();
    } catch { toast.error('Error al eliminar'); }
  };

  const st = res ? (STATUS_CFG[res.status] || STATUS_CFG.free) : STATUS_CFG.free;

  return (
    <div className="w-64 bg-white border-l flex flex-col shadow-lg flex-shrink-0 overflow-y-auto">
      <div className="px-4 py-3 border-b flex items-center justify-between">
        <h3 className="font-semibold text-sm text-slate-800">Propiedades</h3>
        <button onClick={onClose} className="p-1 rounded hover:bg-slate-100">
          <X className="h-4 w-4 text-slate-400" />
        </button>
      </div>

      <div className="p-4 space-y-4 flex-1">
        {/* Status indicator */}
        <div className="rounded-xl p-3 text-center border"
          style={{ backgroundColor: st.bg, borderColor: st.border }}>
          <div className="font-bold text-lg" style={{ color: st.text }}>{table.name}</div>
          <div className="text-xs mt-0.5" style={{ color: st.text }}>
            {st.label} · {table.capacity_max}p
          </div>
          {res && (
            <div className="mt-2 pt-2 border-t" style={{ borderColor: st.border }}>
              <p className="text-sm font-semibold" style={{ color: st.text }}>{res.guest_name}</p>
              <p className="text-xs" style={{ color: st.text }}>
                {res.time} · {res.party_size} pers.
              </p>
              {res.notes && <p className="text-xs italic mt-1 opacity-70" style={{ color: st.text }}>{res.notes}</p>}
            </div>
          )}
        </div>

        {/* Position */}
        <div>
          <Label className="text-[10px] uppercase tracking-wider text-slate-400 mb-1.5 block">Posición</Label>
          <div className="grid grid-cols-2 gap-2 text-xs text-slate-600">
            <div className="bg-slate-50 rounded-lg px-3 py-2">
              <span className="text-slate-400">X: </span><span className="font-mono">{table.x || 0}</span>
            </div>
            <div className="bg-slate-50 rounded-lg px-3 py-2">
              <span className="text-slate-400">Y: </span><span className="font-mono">{table.y || 0}</span>
            </div>
            <div className="bg-slate-50 rounded-lg px-3 py-2">
              <span className="text-slate-400">Rot: </span><span className="font-mono">{table.rotation || 0}°</span>
            </div>
            <div className="bg-slate-50 rounded-lg px-3 py-2">
              <span className="text-slate-400">Ancho: </span><span className="font-mono">{table.width || 80}</span>
            </div>
          </div>
        </div>

        {/* Edit form */}
        {editing ? (
          <div className="space-y-3">
            <div>
              <Label className="text-xs text-slate-500 mb-1 block">Nombre</Label>
              <Input value={form.name} onChange={e => setForm(f => ({ ...f, name: e.target.value }))} className="text-sm h-8" />
            </div>
            <div className="grid grid-cols-2 gap-2">
              <div>
                <Label className="text-xs text-slate-500 mb-1 block">Cap. mín.</Label>
                <Input type="number" min={1} value={form.capacity_min}
                  onChange={e => setForm(f => ({ ...f, capacity_min: +e.target.value }))} className="text-xs h-8" />
              </div>
              <div>
                <Label className="text-xs text-slate-500 mb-1 block">Cap. máx.</Label>
                <Input type="number" min={1} value={form.capacity_max}
                  onChange={e => setForm(f => ({ ...f, capacity_max: +e.target.value }))} className="text-xs h-8" />
              </div>
            </div>
            <div>
              <Label className="text-xs text-slate-500 mb-1 block">Zona</Label>
              <Select value={form.zone_id || 'none'} onValueChange={v => setForm(f => ({ ...f, zone_id: v === 'none' ? '' : v }))}>
                <SelectTrigger className="text-xs h-8"><SelectValue placeholder="Sin zona" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">Sin zona</SelectItem>
                  {zones.map(z => <SelectItem key={z.id} value={z.id}>{z.name}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div className="flex gap-2">
              <Button size="sm" variant="outline" className="flex-1 h-8 text-xs" onClick={() => setEditing(false)}>
                Cancelar
              </Button>
              <Button size="sm" className="flex-1 h-8 text-xs bg-orange-500 hover:bg-orange-600 text-white"
                onClick={save} disabled={saving}>
                {saving ? <Loader2 className="h-3 w-3 animate-spin" /> : 'Guardar'}
              </Button>
            </div>
          </div>
        ) : (
          <div className="space-y-2">
            <Button size="sm" variant="outline" className="w-full h-8 text-xs justify-start gap-2"
              onClick={() => setEditing(true)}>
              <Pencil className="h-3.5 w-3.5 text-slate-400" /> Editar propiedades
            </Button>
          </div>
        )}
      </div>

      {/* Footer actions */}
      <div className="p-4 border-t space-y-2">
        <Button size="sm" variant="outline" className="w-full h-8 text-xs text-red-500 hover:text-red-700 hover:bg-red-50 border-red-200"
          onClick={handleDelete}>
          <Trash2 className="h-3.5 w-3.5 mr-1.5" /> Eliminar mesa
        </Button>
      </div>
    </div>
  );
}

// ─── Main Component ───────────────────────────────────────────────────────────
export default function FloorPlan() {
  const { selectedDate, setSelectedDate } = useAppStore();
  const [tables, setTables] = useState([]);
  const [zones, setZones] = useState([]);
  const [reservations, setReservations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [mode, setMode] = useState('view');         // 'view' | 'edit'
  const [tool, setTool] = useState('select');        // 'select' | 'pan'
  const [selectedId, setSelectedId] = useState(null);
  const [hoveredId, setHoveredId] = useState(null);
  const [zoom, setZoom] = useState(1);
  const [pan, setPan] = useState({ x: 0, y: 0 });
  const [showGrid, setShowGrid] = useState(true);
  const [showAddDialog, setShowAddDialog] = useState(false);
  const [showZonesDialog, setShowZonesDialog] = useState(false);
  const [filterZone, setFilterZone] = useState('all');
  const [date, setDate] = useState(selectedDate || format(new Date(), 'yyyy-MM-dd'));
  const [newTablePos, setNewTablePos] = useState({ x: 200, y: 200 });

  const canvasRef = useRef(null);
  const dragRef = useRef(null);
  const panRef = useRef(null);

  // ── Fetch ───────────────────────────────────────────────────────────────────
  const fetchAll = useCallback(async () => {
    setLoading(true);
    try {
      const [tb, zn, rs] = await Promise.all([
        tablesAPI.list(),
        zonesAPI.list(),
        reservationsAPI.getByDate(date),
      ]);
      setTables(tb.data || []);
      setZones(zn.data || []);
      setReservations(rs.data || []);
    } catch { toast.error('Error al cargar datos'); }
    finally { setLoading(false); }
  }, [date]);

  useEffect(() => { fetchAll(); }, [fetchAll]);

  // ── Reservation map ─────────────────────────────────────────────────────────
  const resMap = useMemo(() => {
    const m = {};
    (reservations || []).forEach(r => { if (r.table_id) m[r.table_id] = r; });
    return m;
  }, [reservations]);

  // ── Selected table ──────────────────────────────────────────────────────────
  const selectedTable = useMemo(() => tables.find(t => t.id === selectedId), [tables, selectedId]);

  // ── Filtered tables ─────────────────────────────────────────────────────────
  const visibleTables = useMemo(() =>
    filterZone === 'all' ? tables : tables.filter(t => t.zone_id === filterZone),
    [tables, filterZone]);

  // ── Stats ───────────────────────────────────────────────────────────────────
  const stats = useMemo(() => {
    const o = tables.filter(t => resMap[t.id]?.status === 'seated').length;
    const c = tables.filter(t => resMap[t.id]?.status === 'confirmed').length;
    const p = tables.filter(t => resMap[t.id]?.status === 'pending').length;
    const tot = tables.length;
    return { occupied: o, confirmed: c, pending: p, free: tot - o - c - p, total: tot,
      pct: tot > 0 ? Math.round(((o + c) / tot) * 100) : 0 };
  }, [tables, resMap]);

  // ── Drag (table move) ───────────────────────────────────────────────────────
  const handleDragStart = useCallback((e, id) => {
    if (mode !== 'edit' || tool !== 'select') return;
    e.preventDefault();
    const t = tables.find(t => t.id === id);
    if (!t) return;
    dragRef.current = { id, sx: e.clientX, sy: e.clientY, ox: t.x || 60, oy: t.y || 60 };
    const onMove = mv => {
      if (!dragRef.current) return;
      const dx = (mv.clientX - dragRef.current.sx) / zoom;
      const dy = (mv.clientY - dragRef.current.sy) / zoom;
      setTables(prev => prev.map(t => t.id === id
        ? { ...t, x: Math.max(0, snap(dragRef.current.ox + dx)), y: Math.max(0, snap(dragRef.current.oy + dy)) }
        : t));
    };
    const onUp = () => {
      const movedId = dragRef.current?.id;
      dragRef.current = null;
      window.removeEventListener('mousemove', onMove);
      window.removeEventListener('mouseup', onUp);
      if (movedId) {
        setTables(prev => {
          const moved = prev.find(t => t.id === movedId);
          if (moved) tablesAPI.update(movedId, { x: moved.x, y: moved.y, rotation: moved.rotation }).catch(() => {});
          return prev;
        });
      }
    };
    window.addEventListener('mousemove', onMove);
    window.addEventListener('mouseup', onUp);
  }, [mode, tool, tables, zoom]);

  // ── Pan canvas ──────────────────────────────────────────────────────────────
  const handleCanvasMouseDown = useCallback((e) => {
    if (e.target === canvasRef.current || tool === 'pan') {
      setSelectedId(null);
      panRef.current = { sx: e.clientX - pan.x, sy: e.clientY - pan.y };
      const onMove = mv => {
        if (!panRef.current) return;
        setPan({ x: mv.clientX - panRef.current.sx, y: mv.clientY - panRef.current.sy });
      };
      const onUp = () => { panRef.current = null; window.removeEventListener('mousemove', onMove); window.removeEventListener('mouseup', onUp); };
      window.addEventListener('mousemove', onMove);
      window.addEventListener('mouseup', onUp);
    }
  }, [pan, tool]);

  // ── Wheel zoom ──────────────────────────────────────────────────────────────
  const handleWheel = useCallback((e) => {
    e.preventDefault();
    const delta = e.deltaY > 0 ? 0.9 : 1.1;
    setZoom(z => Math.min(2.5, Math.max(0.3, z * delta)));
  }, []);

  useEffect(() => {
    const el = canvasRef.current;
    if (!el) return;
    el.addEventListener('wheel', handleWheel, { passive: false });
    return () => el.removeEventListener('wheel', handleWheel);
  }, [handleWheel]);

  // ── Keyboard shortcuts ──────────────────────────────────────────────────────
  useEffect(() => {
    const handler = (e) => {
      if (e.key === 'Escape') { setSelectedId(null); setTool('select'); }
      if (e.key === 'Delete' && selectedId && mode === 'edit') {
        if (window.confirm(`¿Eliminar ${selectedTable?.name}?`)) {
          tablesAPI.delete(selectedId).then(() => {
            setTables(prev => prev.filter(t => t.id !== selectedId));
            setSelectedId(null);
            toast.success('Mesa eliminada');
          }).catch(() => toast.error('Error al eliminar'));
        }
      }
      if ((e.key === 'r' || e.key === 'R') && selectedId && mode === 'edit') {
        setTables(prev => prev.map(t => t.id === selectedId ? { ...t, rotation: ((t.rotation || 0) + 45) % 360 } : t));
      }
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [selectedId, mode, selectedTable]);

  // ── Save layout ─────────────────────────────────────────────────────────────
  const saveLayout = async () => {
    setSaving(true);
    try {
      await Promise.all(tables.map(t => tablesAPI.update(t.id, { x: t.x, y: t.y, rotation: t.rotation })));
      toast.success('Distribución guardada');
    } catch { toast.error('Error al guardar'); }
    finally { setSaving(false); }
  };

  // ── Canvas double-click → add table ─────────────────────────────────────────
  const handleCanvasDblClick = (e) => {
    if (mode !== 'edit') return;
    const rect = e.currentTarget.getBoundingClientRect();
    const x = (e.clientX - rect.left - pan.x) / zoom;
    const y = (e.clientY - rect.top - pan.y) / zoom;
    setNewTablePos({ x: snap(x), y: snap(y) });
    setShowAddDialog(true);
  };

  if (loading) return (
    <div className="h-screen flex flex-col bg-slate-100">
      <TopBar title="Plano de Sala" helpSection="floor-plan" />
      <div className="flex-1 flex items-center justify-center">
        <div className="text-center">
          <Loader2 className="h-8 w-8 animate-spin text-orange-500 mx-auto mb-3" />
          <p className="text-sm text-slate-500">Cargando plano...</p>
        </div>
      </div>
    </div>
  );

  const TOOLS = [
    { k: 'select', icon: MousePointer2, label: 'Seleccionar (V)' },
    { k: 'pan',    icon: Hand,          label: 'Mover vista (H)' },
  ];

  return (
    <div className="h-screen flex flex-col bg-slate-100 overflow-hidden" data-testid="floor-plan-page">
      {/* ── Top Bar ── */}
      <TopBar
        title="Plano de Sala"
        helpSection="floor-plan"
        actions={
          <div className="flex items-center gap-2">
            {/* Date */}
            <div className="flex items-center gap-1 border rounded-lg px-2 py-1 bg-white shadow-sm">
              <button onClick={() => {
                const d = new Date(date); d.setDate(d.getDate() - 1);
                const nd = format(d, 'yyyy-MM-dd'); setDate(nd); setSelectedDate(nd);
              }} className="hover:bg-slate-100 rounded p-0.5"><ChevronLeft className="h-3.5 w-3.5" /></button>
              <input type="date" value={date} onChange={e => { setDate(e.target.value); setSelectedDate(e.target.value); }}
                className="text-xs border-0 bg-transparent outline-none cursor-pointer" />
              <button onClick={() => {
                const d = new Date(date); d.setDate(d.getDate() + 1);
                const nd = format(d, 'yyyy-MM-dd'); setDate(nd); setSelectedDate(nd);
              }} className="hover:bg-slate-100 rounded p-0.5"><ChevronRight className="h-3.5 w-3.5" /></button>
            </div>

            {/* View/Edit toggle */}
            <div className="flex bg-slate-100 rounded-lg p-0.5 gap-0.5">
              {[{ k: 'view', icon: Eye, l: 'Ver' }, { k: 'edit', icon: Move, l: 'Editar' }].map(m => (
                <button key={m.k} onClick={() => { setMode(m.k); setTool('select'); }}
                  className={cn("flex items-center gap-1.5 px-3 py-1.5 rounded-md text-xs font-medium transition-all",
                    mode === m.k ? "bg-white shadow-sm text-slate-800" : "text-slate-500 hover:text-slate-700")}>
                  <m.icon className="h-3.5 w-3.5" /><span className="hidden sm:inline">{m.l}</span>
                </button>
              ))}
            </div>

            {mode === 'edit' && (
              <>
                <Button size="sm" variant="outline" onClick={() => setShowZonesDialog(true)} className="h-8 text-xs">
                  <Grid3X3 className="h-3.5 w-3.5 mr-1" /> Zonas
                </Button>
                <Button size="sm" onClick={() => setShowAddDialog(true)}
                  variant="outline" className="h-8 text-xs">
                  <Plus className="h-3.5 w-3.5 mr-1" /> Mesa
                </Button>
                <Button size="sm" onClick={saveLayout} disabled={saving}
                  className="h-8 bg-orange-500 hover:bg-orange-600 text-white">
                  {saving ? <Loader2 className="h-3.5 w-3.5 animate-spin mr-1" /> : <Save className="h-3.5 w-3.5 mr-1" />}
                  Guardar
                </Button>
              </>
            )}
          </div>
        }
      />

      <div className="flex flex-1 overflow-hidden">
        {/* ── Left panel ── */}
        <div className="w-52 bg-white border-r flex flex-col shadow-sm flex-shrink-0">
          {/* Occupancy gauge */}
          <div className="p-4 border-b">
            <div className="flex items-center justify-between mb-2">
              <span className="text-[10px] uppercase tracking-wider font-semibold text-slate-400">Ocupación</span>
              <span className="text-lg font-bold text-slate-800">{stats.pct}%</span>
            </div>
            <div className="h-2 bg-slate-100 rounded-full overflow-hidden">
              <div className="h-full rounded-full transition-all duration-500"
                style={{
                  width: `${stats.pct}%`,
                  backgroundColor: stats.pct >= 80 ? '#ef4444' : stats.pct >= 50 ? '#f97316' : '#22c55e'
                }} />
            </div>
            <div className="grid grid-cols-2 gap-1.5 mt-3">
              {[
                { l: 'Libre',    n: stats.free,      c: 'bg-slate-50 text-slate-600' },
                { l: 'Conf.',    n: stats.confirmed,  c: 'bg-emerald-50 text-emerald-700' },
                { l: 'En mesa', n: stats.occupied,   c: 'bg-blue-50 text-blue-700' },
                { l: 'Total',   n: stats.total,      c: 'bg-orange-50 text-orange-700' },
              ].map(s => (
                <div key={s.l} className={cn("rounded-lg p-2 text-center", s.c)}>
                  <div className="text-base font-bold leading-tight">{s.n}</div>
                  <div className="text-[10px] font-medium">{s.l}</div>
                </div>
              ))}
            </div>
          </div>

          {/* Status legend */}
          <div className="p-3 border-b">
            <p className="text-[10px] uppercase tracking-wider font-semibold text-slate-400 mb-2">Estados</p>
            <div className="space-y-1.5">
              {Object.entries(STATUS_CFG).map(([k, v]) => (
                <div key={k} className="flex items-center gap-2">
                  <div className="w-3 h-3 rounded-sm border-2 flex-shrink-0"
                    style={{ backgroundColor: v.bg, borderColor: v.border }} />
                  <span className="text-xs text-slate-600">{v.label}</span>
                  <span className="ml-auto text-xs font-semibold text-slate-500">
                    {tables.filter(t => resMap[t.id]?.status === k).length ||
                     (k === 'free' ? stats.free : 0)}
                  </span>
                </div>
              ))}
            </div>
          </div>

          {/* Zone filter */}
          <div className="p-3 flex-1 overflow-y-auto">
            <p className="text-[10px] uppercase tracking-wider font-semibold text-slate-400 mb-2">Zonas</p>
            <div className="space-y-0.5">
              <button onClick={() => setFilterZone('all')}
                className={cn("w-full text-left px-2 py-1.5 rounded-lg text-xs flex items-center justify-between transition-colors",
                  filterZone === 'all' ? "bg-orange-50 text-orange-700 font-semibold" : "text-slate-600 hover:bg-slate-50")}>
                <span>Todas</span>
                <span className="text-[10px] text-slate-400">{tables.length}</span>
              </button>
              {zones.map(z => (
                <button key={z.id} onClick={() => setFilterZone(filterZone === z.id ? 'all' : z.id)}
                  className={cn("w-full text-left px-2 py-1.5 rounded-lg text-xs flex items-center gap-2 justify-between transition-colors",
                    filterZone === z.id ? "bg-orange-50 text-orange-700 font-semibold" : "text-slate-600 hover:bg-slate-50")}>
                  <div className="flex items-center gap-2">
                    <div className="w-2 h-2 rounded-full flex-shrink-0" style={{ backgroundColor: z.color || '#94a3b8' }} />
                    <span className="truncate">{z.name}</span>
                  </div>
                  <span className="text-[10px] text-slate-400 flex-shrink-0">{tables.filter(t => t.zone_id === z.id).length}</span>
                </button>
              ))}
            </div>
          </div>

          {/* Edit tools */}
          {mode === 'edit' && (
            <div className="p-3 border-t bg-slate-50">
              <p className="text-[10px] uppercase tracking-wider font-semibold text-slate-400 mb-2">Herramientas</p>
              <div className="flex gap-1.5">
                {TOOLS.map(t => (
                  <button key={t.k} onClick={() => setTool(t.k)} title={t.label}
                    className={cn("flex-1 flex flex-col items-center gap-1 py-2 rounded-lg border text-[10px] font-medium transition-all",
                      tool === t.k ? "border-orange-500 bg-orange-50 text-orange-700" : "border-slate-200 text-slate-500 hover:border-slate-300")}>
                    <t.icon className="h-4 w-4" />
                    {t.k === 'select' ? 'Selec.' : 'Mover'}
                  </button>
                ))}
              </div>
              <p className="text-[9px] text-slate-400 mt-2 text-center">
                R=rotar · Del=eliminar · Doble click=nueva mesa
              </p>
            </div>
          )}
        </div>

        {/* ── Canvas ── */}
        <div className="flex-1 flex flex-col overflow-hidden">
          {/* Canvas toolbar */}
          <div className="bg-white border-b px-3 py-1.5 flex items-center gap-1.5">
            <button onClick={() => setZoom(z => Math.max(0.25, z - 0.1))}
              className="p-1.5 rounded hover:bg-slate-100 text-slate-500"><ZoomOut className="h-4 w-4" /></button>
            <span className="text-xs text-slate-500 w-10 text-center font-mono">{Math.round(zoom * 100)}%</span>
            <button onClick={() => setZoom(z => Math.min(2.5, z + 0.1))}
              className="p-1.5 rounded hover:bg-slate-100 text-slate-500"><ZoomIn className="h-4 w-4" /></button>
            <button onClick={() => { setZoom(1); setPan({ x: 0, y: 0 }); }}
              className="text-[11px] text-slate-500 hover:bg-slate-100 px-2 py-1 rounded">1:1</button>
            <div className="w-px h-5 bg-slate-200 mx-1" />
            <button onClick={() => setShowGrid(g => !g)}
              className={cn("flex items-center gap-1 px-2 py-1 rounded text-xs transition-colors",
                showGrid ? "bg-slate-100 text-slate-700" : "text-slate-400 hover:bg-slate-50")}>
              <Grid3X3 className="h-3.5 w-3.5" /> Grilla
            </button>
            <div className="w-px h-5 bg-slate-200 mx-1" />
            <button onClick={() => fetchAll()} className="p-1.5 rounded hover:bg-slate-100 text-slate-400">
              <RefreshCw className="h-3.5 w-3.5" />
            </button>

            {mode === 'edit' && (
              <span className="ml-auto text-[10px] text-amber-600 font-medium bg-amber-50 border border-amber-200 px-2 py-1 rounded-full">
                Modo edición activo
              </span>
            )}
            {mode === 'view' && (
              <span className="ml-auto text-[10px] text-emerald-600 font-medium bg-emerald-50 border border-emerald-200 px-2 py-1 rounded-full">
                {date === format(new Date(), 'yyyy-MM-dd') ? 'En tiempo real' : format(new Date(date + 'T12:00:00'), "d MMM", { locale: es })}
              </span>
            )}
          </div>

          {/* Canvas area */}
          <div
            ref={canvasRef}
            className={cn("flex-1 overflow-hidden relative",
              tool === 'pan' ? "cursor-grab active:cursor-grabbing" : "cursor-default")}
            onMouseDown={handleCanvasMouseDown}
            onDoubleClick={handleCanvasDblClick}
          >
            <div
              className="absolute"
              style={{
                transform: `translate(${pan.x}px, ${pan.y}px) scale(${zoom})`,
                transformOrigin: '0 0',
                width: 2000, height: 1200,
                backgroundImage: showGrid
                  ? `linear-gradient(to right, #e2e8f018 1px, transparent 1px), linear-gradient(to bottom, #e2e8f018 1px, transparent 1px)`
                  : 'none',
                backgroundSize: `${GRID}px ${GRID}px`,
              }}
            >
              {/* Zone backgrounds */}
              {zones.map(z => (
                <ZoneBlock key={z.id} zone={z} tables={visibleTables} />
              ))}

              {/* Tables */}
              {visibleTables.map(t => (
                <TableNode
                  key={t.id}
                  table={t}
                  res={resMap[t.id]}
                  selected={selectedId === t.id}
                  hovered={hoveredId === t.id}
                  onSelect={id => { setSelectedId(id); }}
                  onHover={setHoveredId}
                  onDragStart={handleDragStart}
                  mode={mode}
                />
              ))}

              {/* Empty state */}
              {visibleTables.length === 0 && (
                <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
                  <div className="text-center">
                    <div className="w-20 h-20 rounded-2xl border-2 border-dashed border-slate-200 flex items-center justify-center mx-auto mb-4">
                      <Plus className="h-8 w-8 text-slate-200" />
                    </div>
                    <p className="text-slate-400 font-medium">Sin mesas en el plano</p>
                    {mode === 'edit' && (
                      <p className="text-slate-300 text-sm mt-1">Haz doble clic para agregar una mesa</p>
                    )}
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>

        {/* ── Right: Properties Panel ── */}
        {selectedTable && (
          <PropertiesPanel
            table={selectedTable}
            res={resMap[selectedTable.id]}
            zones={zones}
            onUpdate={fetchAll}
            onDelete={() => { setSelectedId(null); fetchAll(); }}
            onClose={() => setSelectedId(null)}
          />
        )}
      </div>

      {/* Dialogs */}
      <AddTableDialog
        open={showAddDialog}
        onClose={() => setShowAddDialog(false)}
        zones={zones}
        onSave={fetchAll}
        canvasX={newTablePos.x}
        canvasY={newTablePos.y}
      />
      <ZonesDialog
        open={showZonesDialog}
        onClose={() => setShowZonesDialog(false)}
        zones={zones}
        onRefresh={fetchAll}
      />
    </div>
  );
}
